import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Price, { CURRENCY } from '../../components/Price';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import log from '../../utils/logger';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Alert, { ALERT_TYPE } from '../../components/Alert';
import Field from '../../components/Field';
import { PaymentMethod } from '../../models/types';
import Account from '../../models/account/Account';
import Modal from '../../components/Modal';
import Payment from './payment';
import PaymentSuccess from './payment_success';
import PaymentAwaiting from './payment_awaiting';
import PaymentFail from './payment_fail';
import InfoDescription from '../../components/InfoDescription';
import Subdivision from '../../models/subdivision/Subdivision';
import { UserContext } from '../../store/UserProvider';

const PayOrder = ({order, onClose = () => {}}) => {
  const {t} = useTranslation(['common', 'sale', 'order']);

  const repository = useContext(RepositoryContext);
  const userInfo = useContext(UserContext);

  const [errors, setErrors] = useState([]);

  const [paymentMethod, setPaymentMethod] = useState(order.paymentDetails?.method ?? PaymentMethod.ONLINE);
  const [account, setAccount] = useState(null);
  const [subdivision, setSubdivision] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [sessionSubdivision] = userInfo.useSession('subdivision');

  const [paymentAmount, setPaymentAmount] = useState(0);
  const [orderNumber] = useState(order.number);

  const [paymentMessage, setPaymentMessage] = useState('');

  const [showPayment, setShowPayment] = useState(false);
  const handleClosePayment = useCallback(() => {
    setShowPayment(false);
  }, []);
  const handleOpenPayment = useCallback((order) => {
    setShowPayment(true);
  }, []);

  const handleCloseError = (uid) => {
    setErrors(errors.filter((e) => e.uid !== uid));
  };

  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const handlePaymentSuccess = (amount) => {
    setPaymentAmount(amount);
    setShowPaymentSuccess(true);
  }
  const handlePaymentSuccessClose = () => {
    setShowPaymentSuccess(false);
    getOrder();
  };


  const [showPaymentAwaiting, setShowPaymentAwaiting] = useState(false);
  const handlePaymentAwaiting = (amount) => {
    setPaymentAmount(amount);
    setShowPaymentAwaiting(true);
  }
  const handlePaymentAwaitingClose = () => {
    setShowPaymentAwaiting(false);
    getOrder();
  };


  const [showPaymentFail, setShowPaymentFail] = useState(false);
  const handlePaymentFail = (message) => {
    setPaymentMessage(message);
    setShowPaymentFail(true);
  }
  const handlePaymentFailClose = () => setShowPaymentFail(false);


  const getOrder = () => {
    repository.request(API.order.order, { id: order.id }).then((o) => {
      order.status = o.status;
      order.paymentDetails = o.paymentDetails;
      onClose && onClose();
    }).catch((e) => {
      log.error(e);
      let errors = [];
      if (e.error && e.error.toLowerCase() === 'form_error') {
        for (let field in e.fields) {
          errors.push({ code: 'form_error', type: ALERT_TYPE.ERROR, title: t('order:error.form_error'), message: t(`order:error.${field}.${e.fields[field]}`) });
        }
      }
      setErrors(errors);
      onClose && onClose();
    });
  }

  useEffect(() => {
    let methods = Object.values(PaymentMethod);

    if (subdivision && subdivision.disabledPaymentMethods instanceof Array) {
      subdivision.disabledPaymentMethods.forEach((m) => {
        methods = methods.filter((method) => method !== m);
      });
    }

    if (account && account.payments) {
      if (!account.payments.online) {
        methods = methods.filter((method) => method !== PaymentMethod.ONLINE);
      }
      if (!account.payments.card) {
        methods = methods.filter((method) => method !== PaymentMethod.CARD && method !== PaymentMethod.QRCODE);
      }
    }

    setPaymentMethods(methods);
    if (methods.indexOf(paymentMethod) === -1 && methods.length > 0) {
      setPaymentMethod(methods[0]);
    }
  }, [subdivision, account]);

  useEffect(() => {
    repository.request(API.subdivision.subdivision, {id: sessionSubdivision}, Subdivision).then((s) => {
      setSubdivision(s);
    }).catch(() => {
      setSubdivision(null);
    });

    repository.request(API.account.account, {}, Account).then((a) => {
      setAccount(a);
    }).catch(() => {
      setAccount(null);
    });
  }, []);

  return (
    <div className="trip-app">
      <div className="trip-app-body">
        <table className="info-table">
          <tbody>
            <tr>
              <td>
                <InfoDescription description={t('order:new_order.in_check')}>
                  <Price currency={CURRENCY.RUB} value={order.paymentDetails.partialValue} />
                </InfoDescription>
              </td>
              <td>
                <InfoDescription description={t('order:new_order.guide_pay')}>
                  <Price currency={CURRENCY.RUB} value={order.paymentDetails.leftToPay} />
                </InfoDescription>
              </td>
              <td>
                <InfoDescription description={t('order:new_order.total')}>
                  <Price currency={CURRENCY.RUB} value={order.paymentDetails.partialValue + order.paymentDetails.leftToPay} />
                </InfoDescription>
              </td>
            </tr>
          </tbody>
        </table>

        {(errors.length > 0) && errors.map((error, i) => (
          <Alert type={error.type} key={error.uid || i} title={error.title} dismissible={true} onClose={() => handleCloseError(error.uid)}>{error.message}</Alert>
        ))}

        <Field title={t('order:payment_booking.payment_method')}>
          {paymentMethods.indexOf(PaymentMethod.CASH) >= 0 && (
            <div className={ `payment-method icon-${PaymentMethod.CASH}` } onClick={() => setPaymentMethod(PaymentMethod.CASH)}>{t(`order:payment_booking.${PaymentMethod.CASH}`)}{paymentMethod === PaymentMethod.CASH && (<span className="payment-current">{t('order:payment_booking.current')}</span>)}</div>
          )}
          {paymentMethods.indexOf(PaymentMethod.ONLINE) >= 0 && (
            <div className={ `payment-method icon-${PaymentMethod.ONLINE}` } onClick={() => setPaymentMethod(PaymentMethod.ONLINE)}>{t(`order:payment_booking.${PaymentMethod.ONLINE}`)}{paymentMethod === PaymentMethod.ONLINE && (<span className="payment-current">{t('order:payment_booking.current')}</span>)}</div>
          )}
          {paymentMethods.indexOf(PaymentMethod.QRCODE) >= 0 && (
            <div className={ `payment-method icon-${PaymentMethod.QRCODE}` } onClick={() => setPaymentMethod(PaymentMethod.QRCODE)}>{t(`order:payment_booking.${PaymentMethod.QRCODE}`)}{paymentMethod === PaymentMethod.QRCODE && (<span className="payment-current">{t('order:payment_booking.current')}</span>)}</div>
          )}
          {paymentMethods.indexOf(PaymentMethod.CARD) >= 0 && (
            <div className={ `payment-method icon-${PaymentMethod.CARD}` } onClick={() => setPaymentMethod(PaymentMethod.CARD)}>{t(`order:payment_booking.${PaymentMethod.CARD}`)}{paymentMethod === PaymentMethod.CARD && (<span className="payment-current">{t('order:payment_booking.current')}</span>)}</div>
          )}
        </Field>

        <Modal show={order && showPayment} onHide={handleClosePayment}>
          <Modal.Header closeButton>
            {t('order:order_info.title', { order: orderNumber })}
          </Modal.Header>
          <Modal.Body>
            {showPayment && ( /* re-create body*/
              <Payment order={order.id} amount={order.paymentDetails.partialValue} type={paymentMethod} onCancel={handleClosePayment} onSuccess={handlePaymentSuccess} onFail={handlePaymentFail} onAwaiting={handlePaymentAwaiting}/>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={showPaymentSuccess} onHide={handlePaymentSuccessClose}>
          <Modal.Header>{t('order:order_info.title', {order: orderNumber})}</Modal.Header>
          <Modal.Body>
            <PaymentSuccess amount={paymentAmount} />
          </Modal.Body>
          <Modal.Footer>
            <Button.Panel className="button-panel-center">
              <Button position={BUTTON_POSITION.CENTER} variant={BUTTON_TYPE.PRIMARY} onClick={handlePaymentSuccessClose}>{t('common:button.ok')}</Button>
            </Button.Panel>
          </Modal.Footer>
        </Modal>

        <Modal show={showPaymentAwaiting} onHide={handlePaymentAwaitingClose}>
          <Modal.Header>{t('order:order_info.title', {order: orderNumber})}</Modal.Header>
          <Modal.Body>
            <PaymentAwaiting amount={paymentAmount} />
          </Modal.Body>
          <Modal.Footer>
            <Button.Panel className="button-panel-center">
              <Button position={BUTTON_POSITION.CENTER} variant={BUTTON_TYPE.PRIMARY} onClick={handlePaymentAwaitingClose}>{t('common:button.ok')}</Button>
            </Button.Panel>
          </Modal.Footer>
        </Modal>

        <Modal show={showPaymentFail} onHide={handlePaymentFailClose}>
          <Modal.Header>{t('order:order_info.title', {order: orderNumber})}</Modal.Header>
          <Modal.Body>
            <PaymentFail message={paymentMessage} />
          </Modal.Body>
          <Modal.Footer>
            <Button.Panel className="button-panel-center">
              <Button position={BUTTON_POSITION.CENTER} variant={BUTTON_TYPE.PRIMARY} onClick={handlePaymentFailClose}>{t('common:button.ok')}</Button>
            </Button.Panel>
          </Modal.Footer>
        </Modal>
      </div>
      <div className="trip-app-footer">
        <Button.Panel>
          <Button position={BUTTON_POSITION.LEFT} onClick={handleOpenPayment}>{t('common:button.pay')}</Button>
          <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={onClose}>{t('common:button.cancel')}</Button>
        </Button.Panel>
      </div>
    </div>
  )
}

PayOrder.propTypes = {
  order: PropTypes.object.isRequired
};

export default PayOrder;